import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../../services/Toast.service";
import {TAX_PLANNING_TYPES} from "../../../constants/tax-planning.constants";
import {NumeralService} from "../../../services/Numeral.service";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {Session} from "../../../services/Session";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {ModelBasedNumeral} from "../../../directives/model-numeral";
import {TpiqCustomDatepicker} from "../../../directives/tpiq-custom-datepicker";

declare const _: any;

@Component({
  selector: 'app-cashflow-lines',
  standalone: true,
  imports: [CommonModule, FormsModule, ModelBasedNumeral, TpiqSelectDropdownComponent, TpiqCustomDatepicker],
  templateUrl: './cashflow-lines.component.html',
  styleUrl: './cashflow-lines.component.scss'
})
export class CashflowLinesComponent implements OnInit, OnDestroy {
  @Input() client: any;
  @Input() cashflowLines: any;
  @Input() cashflowStrategies: any;
  @Input() thirdPartiesList: any;
  @Input() taxStrategyId: any;
  @Input() freezPlan: any;
  @Input() userReadOnly: any;
  @Output() onCreationOrUpdateOfCashflow: EventEmitter<any> = new EventEmitter<any>();

  cashflowTableData: any = {};
  org: string = Session.getOrg();
  user: any = {};
  columns: Array<any> = [
    {name: 'type', title: 'Type', type: 'html', classes: 'strategy-column pointer zero-overflow table-header-badge-tooltip', sortable: true},
    {name: 'strategyName', title: 'Strategy', type: 'html', classes: 'strategy-column pointer zero-overflow table-header-badge-tooltip', sortable: true},
    {name: 'description', title: 'Description', type: 'normal', classes: 'type-column pointer zero-overflow table-header-badge-tooltip', sortable: true},
    {name: 'amount', title: 'Amount', type: 'normal', classes: 'client-column pointer zero-overflow tracker-desc table-header-badge-tooltip', sortable: true},
    {name: 'transactionType', title: 'Transaction Type', type: 'html', classes: 'strategy-column pointer zero-overflow table-header-badge-tooltip', sortable: true},
    {name: 'thirdPartyName', title: 'Third Party', type: 'html', classes: 'strategy-column pointer zero-overflow table-header-badge-tooltip', sortable: true},
    {name: 'frequency', title: 'Frequency', type: 'badge-input-search', 'status-col': true, classes: 'associated-column pointer zero-overflow tracker-desc table-header-badge-tooltip', sortable: true},
    {name: 'paymentDate', title: 'Payment Date', type: 'badge-input-search', 'status-col': true, classes: 'associated-column pointer zero-overflow tracker-desc table-header-badge-tooltip', sortable: true},
  ];
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  reveal: any = {
    addNewCashflowLine: false
  };
  cashflowLine = {};
  cashflowTypes: Array<any> = [{name: 'One Time'}, {name: 'Recurring'}];
  transactionTypes: Array<any> = [{name: 'Sender'}, {name: 'Receiver'}];
  cashflowFrequencyTypes: Array<any> = [{name: 'Monthly'}, {name: 'Yearly'}];
  editMode: boolean = false;
  currentEditRowData: any;
  isNewThirdpartyAdd: boolean = false;
  types: Array<any> = TAX_PLANNING_TYPES;
  thirdParty: any = {
    name: '',
    type: ''
  };
  currentPlan: string;

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private numeralService: NumeralService
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    this.getSubscriptionInfo();
    this.formRowsDataForTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  formRowsDataForTable() {
    const data = _.cloneDeep(this.cashflowLines);
    if (data && data.length > 0) {
      _.each(data, (line) => {
        line.amount = this.numeralService.format('$0,0', line.amount);
        const obj = _.find(this.cashflowStrategies, {strategyId: line.strategyId});
        line.strategyName = (obj) ? obj.strategyName : '';
        const thirdPartyObj = _.find(this.thirdPartiesList, {id: line.thirdPartyId});
        line.thirdPartyName = (thirdPartyObj) ? thirdPartyObj.name : '';
      });
    }
    this.cashflowTableData = data;
  }


  hideReveal(key){
    this.cashflowLine = {};
    this.editMode = false;
    this.currentEditRowData = '';
    this.reveal[key] = false;
  }

  addNewCashflowLine() {
    this.reveal.addNewCashflowLine = true;
    this.cashflowLine = {
      planId: this.taxStrategyId,
      thirdPartyId: '',
      frequency: 'Yearly',
    };
  }

  setStrategyForCashflow($event, cashflowLine) {
    cashflowLine.strategyId = $event.strategyId;
  }

  setThirdPartyForCashflow($event, cashflowLine) {
    cashflowLine.thirdPartyId = $event.id;
  }

  setPeriodForCashflow($event, cashflowLine){
    cashflowLine.frequency = $event.name;
  }

  setPaymentDate($event, cashflowLine) {
    if ($event) {
      cashflowLine.paymentDate = $event;
    }
  }


  setCashflowType($event, cashflowLine){
    cashflowLine.type = $event.name;
  }

  setCashflowTransactionType($event, cashflowLine){
    cashflowLine.transactionType = $event.name;
  }


  saveCashflow() {
    const cashflowinesData = _.cloneDeep(this.cashflowLines);
    if (!this.editMode) {
      cashflowinesData.push(this.cashflowLine);
    } else if (this.editMode) {
      const lineIndex = _.findIndex(cashflowinesData, {id: this.currentEditRowData.id});
      if (lineIndex != -1) {
        cashflowinesData[lineIndex] = this.cashflowLine;
      }
    }

    if (!this.validateCashflowLines(cashflowinesData)){
      return false;
    }
    this.saveCashflowLines(cashflowinesData);
  }

  validateCashflowLines(cashflowData){
    const base = this;
    let isFineToSave = true;
    _.each(cashflowData, (cashflow) => {
      if (!cashflow.type) {
        base.toastService.pop(TOAST_TYPE.error, 'Type cannot be empty');
        isFineToSave = false;
      }
      if (!cashflow.strategyId) {
        base.toastService.pop(TOAST_TYPE.error, 'Strategy cannot be empty');
        isFineToSave = false;
      }
      if (!cashflow.transactionType) {
        base.toastService.pop(TOAST_TYPE.error, 'Transaction type cannot be empty');
        isFineToSave = false;
      }
      if (!cashflow.thirdPartyId) {
        base.toastService.pop(TOAST_TYPE.error, 'Third Party cannot be empty');
        isFineToSave = false;
      }
    });
    return isFineToSave;
  }

  saveCashflowLines(data) {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.updateCashflowLines(this.org, this.user.id, this.taxStrategyId, data)
      .subscribe({
        next: (response) => {
          this.loadingService.triggerLoadingEvent(false);
          this.cashflowLines = response;
          this.formRowsDataForTable();
          this.hideReveal('addNewCashflowLine');
          this.onCreationOrUpdateOfCashflow.emit();
          this.toastService.pop(TOAST_TYPE.success, 'Updated successfully');
        },
        error: (error) => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.error, 'Failed to Update');
        }
      });
  }

  deleteCashflowLine(index) {
    this.cashflowTableData.splice(index, 1);
    this.cashflowLines.splice(index, 1);
    this.saveCashflowLines(this.cashflowLines);
  }

  editCashflow(row, index) {
    this.editMode = true;
    this.reveal.addNewCashflowLine = true;
    this.cashflowLine = _.cloneDeep(row);
    this.currentEditRowData = row;
  }

  toggleAddThirdparty(){
    this.isNewThirdpartyAdd = !this.isNewThirdpartyAdd;
  }

  addNewThirdpartyToList() {
    if(!this.thirdParty.name) {
      this.toastService.pop(TOAST_TYPE.error, 'Please enter third party name');
    } else if (!this.thirdParty.type) {
      this.toastService.pop(TOAST_TYPE.error, 'Please enter type');
    } else {
      this.thirdPartiesList.push(this.thirdParty);
      this.client.thirdParties = this.thirdPartiesList;
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.updateClient(this.org, this.user.id, this.client.id, this.client).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.thirdParty = { name: '', type: ''};
        this.toggleAddThirdparty();
        this.taxPlanningService.clearClientData();
        this.toastService.pop(TOAST_TYPE.success, 'Third party added successfully');
        this.getClientData();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try{
          this.toastService.pop(TOAST_TYPE.error, error.message ? error.message : 'Failed to add third party');
        } catch (e){
          this.toastService.pop(TOAST_TYPE.error, 'Failed to add third party');
        }
      });
    }
  }

  getClientData() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClient(this.org, this.user.id, this.client.id).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.client = response;
      this.thirdPartiesList = response.thirdParties || [];
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      try{
        this.toastService.pop(TOAST_TYPE.error, error.message ? error.message : 'Failed to get client data');
      } catch (e){
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get client data');
      }
    });
  }

  setType($event) {
    this.thirdParty.type = $event.name;
  }

  getSubscriptionInfo() {
    this.taxPlanningService.getSubscriptionStatus(this.org, this.user.id).subscribe(subscription => {
      this.currentPlan = subscription.plan;
    });
  }

}
